import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../pages/Home.css';
import useImageLazyLoad from '../hooks/useImageLazyLoad';
import FeaturedProductCard from '../components/FeaturedProductCard';
import SkeletonScreen from '../components/SkeletonScreen';
import API from '../api';
import { safeEncodeURIComponent, generateProductUrl } from '../utils/urlEncoding';
import { useIsClient } from '../utils/hydrationSafe';
import { trackEvent } from '../utils/analytics';
import { generateCategoryUrl } from '../utils/enhancedLinkGenerators';

// Import Hero images - optimized versions
import heroCanadaWebpOptimized from '../assets/Hero_canada_temp_1200_q40.webp';
import heroCanadaAvif from '../assets/Hero_canada.avif';

// Import mobile hero images - optimized versions
import heroCanadaMobilePng from '../assets/mobile/Hero_canada_mobile.png'; 
import heroCanadaMobileWebpOptimized from '../assets/mobile/Hero_canada_mobile_optimized_fixed.webp';
import heroCanadaMobileAvif from '../assets/mobile/Hero_canada_mobile.avif';

// Chrome logo from Iconify - high quality brand logo with proper colors
const chromeLogoUrl = 'https://api.iconify.design/logos:chrome.svg';

/**
 * Root Home Component - Renders home page for the root path "/" 
 * This is used for SEO prerendering with specific canonical URLs and content
 */
function RootHome() {
  // For the root path, default to Canada
  const countryCode = 'canada';
  const navigate = useNavigate();
  const location = useLocation();
  
  // This is always the root path
  const isRootPath = true;

  // Immediate fallback products for instant display (using skeleton placeholders)
  const immediateFallbackProducts = [
    {
      _id: 'skeleton-1',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-2',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-3',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    },
    {
      _id: 'skeleton-4',
      name: 'Loading...',
      shortDescription: 'Featured product loading...',
      imageUrl: null,
      price: null,
      isSkeletonPlaceholder: true
    }
  ];

  // Local state for Home page UI
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [featuredProducts, setFeaturedProducts] = useState(immediateFallbackProducts);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('');
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  const isClient = useIsClient();

  // Ensure consistent initial render for SSR hydration
  useEffect(() => {
    if (!isClient) return;
    
    const timer = setTimeout(() => {
      setContentLoaded(true);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [isClient]);

  // Handler for the hero search bar
  const handleHeroSearchSubmit = (e) => {
    e.preventDefault();
    if (heroSearchQuery.trim()) {
      const trimmedQuery = heroSearchQuery.trim();
      
      // Track hero search in Google Analytics
      trackEvent('search', {
        event_category: 'Search',
        event_label: `Search Term: ${trimmedQuery}`,
        search_term: trimmedQuery,
        search_source: 'root_home_hero',
        country_code: countryCode
      });
      
      // For root path, redirect to Canada search
      if (typeof window !== 'undefined') {
        window.location.href = `/country/canada/search?q=${safeEncodeURIComponent(trimmedQuery)}`;
      }
      setHeroSearchQuery('');
    }
  };

  // Handler for category buttons
  const handleHeroCategoryClick = (filterType) => {
    setActiveCategoryFilter(filterType);
    
    trackEvent('click', {
      event_category: 'Navigation',
      event_label: `Root Hero Category Filter: ${filterType}`,
      event_action: 'category_filter_click',
      button_location: 'root_hero_section',
      filter_type: filterType,
      country_code: countryCode
    });
    
    // Map filter types to actual categories and navigate to Canada products
    const categoryMapping = {
      'Baby & Kids': 'Baby & Kids',
      'Gourmet': 'Grocery & Gourmet Foods',
      'Home Decor': 'Home Decor',
      'Beauty': 'Beauty & Personal Care',
      'Books': 'Books & Media',
      'Clothing': 'Clothing',
      'Wellness': 'Health & Wellness',
      'Beverage': 'Beverages'
    };
    
    const category = categoryMapping[filterType];
    
    if (category) {
      try {
        const cleanUrl = generateCategoryUrl('canada', category);
        const urlWithFresh = `${cleanUrl}?fresh=true`;
        navigate(urlWithFresh);
      } catch (error) {
        console.error('ERROR generating clean URL:', error);
        const targetUrl = generateCategoryUrl('canada', category) + '?fresh=true';
        navigate(targetUrl);
      }
    }
  };

  return (
    <>
      <Helmet>
        {/* Root path SEO - more generic, international */}
        <title>OriginSelect - Shop Canadian-Made Goods Anywhere | Quality Products Made with Heart & Heritage</title>
        <meta 
          name="description" 
          content="Discover 3,000+ products crafted by Canadian brands. Shop sustainable baby products, artisan goods, and authentic local items curated for discerning customers."
        />
        
        {/* Enhanced Open Graph tags for root path */}
        <meta property="og:title" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
        <meta property="og:description" content="Discover 3,000+ products crafted by Canadian brands. Shop sustainable, artisan-made goods." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://originselect.com/" />
        <meta property="og:image" content="https://originselect.com/Hero_canada.avif" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="OriginSelect" />
        
        {/* Enhanced Twitter Card tags for root path */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@OriginSelect" />
        <meta name="twitter:title" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
        <meta name="twitter:description" content="Discover 3,000+ products crafted by Canadian brands." />
        <meta name="twitter:image" content="https://originselect.com/Hero_canada.avif" />
        <meta name="twitter:image:alt" content="OriginSelect - Shop Canadian-Made Goods Anywhere" />
        
        {/* Canonical URL for root path */}
        <link rel="canonical" href="https://originselect.com/" />
        
        {/* Hreflang pairs for root path */}
        <link rel="alternate" hreflang="en-ca" href="https://originselect.com/country/canada/home/" />
        <link rel="alternate" hreflang="en-us" href="https://originselect.com/country/usa/home/" />
        <link rel="alternate" hreflang="x-default" href="https://originselect.com/" />
        
        {/* Common SEO tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        
        {/* Preconnect to external domains for faster resource loading */}
        <link rel="preconnect" href="https://api.iconify.design" />
        <link rel="preconnect" href="https://apps.apple.com" />
        <link rel="preconnect" href="https://chrome.google.com" />
        
        {/* Preload critical hero image */}
        <link 
          rel="preload" 
          as="image" 
          href={heroCanadaWebpOptimized} 
          fetchpriority="high"
          type="image/webp"
        />
        
        {/* JSON-LD Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "OriginSelect",
            "url": "https://originselect.com",
            "logo": "https://originselect.com/static/logo.png",
            "description": "OriginSelect connects you with authentic, handcrafted products from local artisans. Discover unique, high-quality goods made with heart and heritage.",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "CA"
            },
            "sameAs": [
              "https://www.pinterest.com/originselect_canada/",
              "https://www.instagram.com/OriginSelect/",
              "https://www.youtube.com/@OriginSelect"
            ]
          })}
        </script>
      </Helmet>

      {/* Always render skeleton to ensure consistent server/client rendering */}
      <div style={{ 
        opacity: contentLoaded ? 0 : 1, 
        visibility: contentLoaded ? 'hidden' : 'visible',
        position: contentLoaded ? 'absolute' : 'relative',
        transition: 'opacity 0.3s ease'
      }}>
        <SkeletonScreen 
          type="home" 
          count={4} 
          isVisible={true} 
        />
      </div>

      {/* Always render main content but control visibility with CSS */}
      <div 
        className="home-page" 
        style={{
          opacity: contentLoaded ? 1 : 0, 
          visibility: contentLoaded ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease'
        }}
      >
        {/* Hero Section */}
        <section className="hero-section">
          <div className={`hero-image-container ${heroImageLoaded ? 'loaded' : ''}`}>
            <picture className="hero-flag-bg-picture">
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobileAvif} 
                type="image/avif" 
                fetchpriority="high"
              />
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobileWebpOptimized} 
                type="image/webp" 
                fetchpriority="high"
              />
              <source 
                media="(max-width: 767px)" 
                srcSet={heroCanadaMobilePng} 
                type="image/png" 
              />
              <source srcSet={heroCanadaAvif} type="image/avif" fetchpriority="high" />
              <source srcSet={heroCanadaWebpOptimized} type="image/webp" fetchpriority="high" />
              <img 
                src={heroCanadaWebpOptimized}
                alt="Canada scenic background showcasing local landscape and culture"
                className="hero-flag-img"
                loading="eager" 
                fetchpriority="high"
                decoding="sync"
                width="1920" 
                height="1080"
                onLoad={() => setHeroImageLoaded(true)}
                onError={() => setHeroImageLoaded(true)}
              />
            </picture>
          </div>
          <div className="hero-content">
            <h1>Handpicked Canadian Goods</h1>
            <p>From local workshops to your doorstep</p>

            {/* Hero Search Bar */}
            <form onSubmit={handleHeroSearchSubmit} className="hero-search-form">
              <input
                type="text"
                className="hero-search-input"
                placeholder="Search products..."
                value={heroSearchQuery}
                onChange={(e) => setHeroSearchQuery(e.target.value)}
                aria-label="Search products in hero section"
              />
              <button type="submit" className="hero-search-button" aria-label="Submit search">
                <svg className="search-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </button>
            </form>

            {/* Category Filter Buttons */}
            <div className="hero-category-filters" role="tablist" aria-label="Product category filters">
              <button 
                id="category-filter-baby-kids"
                role="tab"
                aria-selected={activeCategoryFilter === 'Baby & Kids'}
                aria-controls="category-content-baby-kids"
                tabIndex={activeCategoryFilter === 'Baby & Kids' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Baby & Kids' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Baby & Kids')}
              >
                Baby & Kids
              </button>
              <button 
                id="category-filter-gourmet"
                role="tab"
                aria-selected={activeCategoryFilter === 'Gourmet'}
                aria-controls="category-content-gourmet"
                tabIndex={activeCategoryFilter === 'Gourmet' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Gourmet' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Gourmet')}
              >
                Gourmet
              </button>
              <button 
                id="category-filter-home-decor"
                role="tab"
                aria-selected={activeCategoryFilter === 'Home Decor'}
                aria-controls="category-content-home-decor"
                tabIndex={activeCategoryFilter === 'Home Decor' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Home Decor' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Home Decor')}
              >
                Home Decor
              </button>
              <button 
                id="category-filter-beauty"
                role="tab"
                aria-selected={activeCategoryFilter === 'Beauty'}
                aria-controls="category-content-beauty"
                tabIndex={activeCategoryFilter === 'Beauty' ? 0 : -1}
                className={`category-filter-btn ${activeCategoryFilter === 'Beauty' ? 'active' : ''}`}
                onClick={() => handleHeroCategoryClick('Beauty')}
              >
                Beauty
              </button>
            </div>

            <div className="hero-buttons-container">
              <Link
                to="/country/canada/products?reset=true"
                className="hero-shop-now-btn"
              >
                Shop All
              </Link>
              
              <a
                href="https://apps.apple.com/us/app/originselect/id6744981976"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-app-store-link"
                aria-label="Download OriginSelect on the App Store"
              >
                <img
                  className="app-store-badge"
                  src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-download-on-the-app-store.svg"
                  alt="Download OriginSelect on the App Store"
                />
              </a>
            </div>
          </div>
        </section>

        <section className="featured-products-section">
          <h2>Featured Products</h2>
          <div className="product-grid">
            {immediateFallbackProducts.map((product, index) => (
              <div key={product._id} className="featured-product-card skeleton-placeholder" tabindex="-1" role="presentation" aria-label="Loading product...">
                <div className="featured-product-image-container">
                  <div className="skeleton-image-placeholder">
                    <div className="skeleton-shimmer"></div>
                  </div>
                </div>
                <div className="featured-product-info">
                  <div className="skeleton-text skeleton-title"></div>
                  <div className="skeleton-text skeleton-price"></div>
                  <div className="skeleton-button"></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="about-section">
          <h2>Our Mission</h2>
          <p>
            Championing products made with heart and heritage.
            OriginSelect connects you with locally sourced excellence.
          </p>
          <Link
            to="/country/canada/about"
            className="btn secondary-btn"
          >
            Learn More
          </Link>
        </section>

        {/* Hidden content areas for screen reader aria-controls references */}
        <div className="sr-only" id="category-content-baby-kids" role="tabpanel" aria-labelledby="category-filter-baby-kids">
          Baby & Kids products section
        </div>
        <div className="sr-only" id="category-content-gourmet" role="tabpanel" aria-labelledby="category-filter-gourmet">
          Gourmet products section
        </div>
        <div className="sr-only" id="category-content-home-decor" role="tabpanel" aria-labelledby="category-filter-home-decor">
          Home decor products section
        </div>
        <div className="sr-only" id="category-content-beauty" role="tabpanel" aria-labelledby="category-filter-beauty">
          Beauty products section
        </div>
      </div>
    </>
  );
}

export default RootHome;
